// Questionnaire/PersonalInfo.tsx

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {Box, Checkbox, FormControl, FormControlLabel, Typography} from '@mui/material';
import SkillsPicker from 'Psychologist/SkillsPicker';
import SpecializationsPicker from 'Psychologist/SpecializationsPicker';
import {IConsultationConfig} from '../../Types/psychology/consultation';
import {FC} from 'WideLayout/Layouts';
import pprint from '../../Utils/pprint';
import RadioLine from 'Core/components/elements/RadioLine';
import CustomDateTimePicker from 'Core/components/elements/CustomDateTimePicker';
import {useTheme} from 'Theme/ThemeContext';
import BottomButton from "Core/components/BottomButton";
import {Message} from "Core/components/Message";
import Button from "Core/components/elements/Button/Button";
import {delFilterFromClientRequest} from "../Questionnaire/clientRequest";
import Head from "Core/components/Head";
import {DOMAIN_URL} from "Auth/axiosConfig";
import AgePicker from "Questionnaire/AgePicker";
import ProfPicker from "Questionnaire/ProfPicker";

interface FormData {
    consultation_config: IConsultationConfig | null;
    comfort_preference: string;
    gender_preference: string;
    age_preference: string;
    session_time: string;
    specific_datetime?: string;
    skills: number[];
    specializations: number[];
    is_psychologist: boolean;
    mental_health: string | number | null;
    for_whom?: string | number | null;
    prof_level?: string | number | null;
    is_psychologist_has_child: 'yes' | 'no' | 'not_important';
}

const PersonalInfo: React.FC = () => {
    const savedData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('clientRequest') || '{}');
        } catch (e) {
            console.error('Ошибка при парсе clientRequest из localStorage:', e);
            return {};
        }
    }, []);

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: {isValid},
    } = useForm<FormData>({
        mode: 'onChange',
        defaultValues: {
            consultation_config: savedData.consultation_config || null,
            comfort_preference: savedData.comfort_preference || 'any',
            session_time: savedData.session_time || 'any',
            specializations: savedData.specializations || [],
            is_psychologist: savedData.is_psychologist || false,
            skills: savedData.skills || [],
            gender_preference: savedData.gender_preference || '',
            age_preference: savedData.age_preference || '',
            mental_health: savedData.mental_health ?? 'bad',
            prof_level: savedData.prof_level || '',
            for_whom: savedData.for_whom || '',
            is_psychologist_has_child: savedData.is_psychologist_has_child || 'not_important',
            specific_datetime: savedData.specific_datetime || '',
        },
    });
    const {plt} = useTheme();

    const navigate = useNavigate();
    const location = useLocation();
    const comfort_preference = watch('comfort_preference', 'any');
    const session_time = watch('session_time', 'any');
    const _selectedSpecializations = watch('specializations', []);
    const selectedSkills = watch('skills', []);
    const for_whom = watch('for_whom', null);
    const prof_level = watch('prof_level', null);
    const is_psychologist = watch('is_psychologist', false);

    const [specificDateTime, setSpecificDateTime] = useState<Date | null>(
        savedData.specific_datetime ? new Date(savedData.specific_datetime) : null
    );
    const queryParams = new URLSearchParams(location.search);
    const next = queryParams.get('next') || '/psychologists';

    // Создаем ref для контейнера
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Ref для отслеживания, было ли уже выполнено перенаправление
    const hasNavigated = useRef(false);

    useEffect(() => {
        if (hasNavigated.current) return;

        const requiredFields = ['age', 'therapy_experience', 'client_gender', 'timezone',];
        const isDataComplete = requiredFields.every((field) => savedData.hasOwnProperty(field));

        if (!isDataComplete) {
            Message.info('Сначала нужно указать возраст');
            navigate(`/questionnaire/general`);
            hasNavigated.current = true;
        }
    }, [navigate, savedData, location.search]);

    useEffect(() => {
        if (is_psychologist) {
            setValue('comfort_preference', 'specific');
        }
    }, [is_psychologist, setValue]);

    const onSubmit: SubmitHandler<FormData> = (data: FormData) => {
        pprint('data', data);
        const existingData = JSON.parse(localStorage.getItem('clientRequest') || '{}');
        const updatedData = {...existingData};

        updatedData.consultation_config = data.consultation_config;
        updatedData.comfort_preference = data.comfort_preference;
        updatedData.gender_preference = data.gender_preference;
        updatedData.age_preference = data.age_preference;
        updatedData.session_time = data.session_time;
        updatedData.is_psychologist = data.is_psychologist;
        updatedData.skills = data.skills;
        updatedData.specializations = data.specializations;
        if (data.mental_health !== undefined) {
            updatedData.mental_health = data.mental_health;
        }
        updatedData.prof_level = data.prof_level;
        updatedData.for_whom = data.for_whom;
        updatedData.is_psychologist_has_child = data.is_psychologist_has_child;
        if (session_time === 'specific' && specificDateTime) {
            updatedData.specific_datetime = specificDateTime.toISOString();
        } else {
            updatedData.specific_datetime = null;
        }
        localStorage.setItem('clientRequest', JSON.stringify(updatedData));
        navigate(next);
    };

    return (
        <FC
            component="form"
            grow
            scroll={'y-auto'}
            mx={'auto'}
            maxW={'700px'}
            onSubmit={handleSubmit(onSubmit)}
            h={'100%'}
            ref={containerRef}
        >
            <Head
                title={'Личные вопросы | Лайф Хелп'}
                description={'Помощь квалифицированных психологов от сервиса Лайф Хелп'}
                robots={'noindex, nofollow'}
                canonicalUrl={`${DOMAIN_URL}/questionnaire/personal`}
            />
            <FC scroll={'y-auto'} grow h={'100%'} pos={'relative'}>
                <FC g={3} px={1} pt={1} scroll={'y-auto'} pb={'4rem'}>
                    <FC>
                        {/* Checkbox "Я психолог" */}
                        <FormControl component="fieldset" className={'fc d-none gap-1 ms-auto'}>
                            <Controller
                                name="is_psychologist"
                                control={control}
                                render={({field}) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    lineHeight: '1rem',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <span>Я психолог</span>
                                                <span style={{color: plt.text.primary40, fontSize: '0.8em'}}>
                                                    интервизия / супервизия
                                                </span>
                                            </span>
                                        }
                                    />
                                )}
                            />
                        </FormControl>

                        {/* AgePicker */}
                        <FormControl component="fieldset" className={'fc gap-1'}>
                            <Typography variant="h6">Для кого нужен специалист?</Typography>
                            <AgePicker
                                control={control}
                                name="for_whom"
                                required={false}/>
                        </FormControl>

                        {/*/!* MentalHealthPicker *!/*/}
                        {/*<FormControl style={{marginTop: '20px'}} component="fieldset" className={'fc gap-1'}>*/}
                        {/*    <Typography variant="h6">Как вы себя чувствуете?</Typography>*/}
                        {/*    <MentalHealthPicker*/}
                        {/*        control={control}*/}
                        {/*        name="mental_health"*/}
                        {/*        required={true}/>*/}
                        {/*</FormControl>*/}

                        {/* MentalHealthPicker */}
                        <FormControl style={{marginTop: '20px'}} component="fieldset" className={'fc gap-1'}>
                            <Typography variant="h6">Уточнения по специалисту:</Typography>
                            <ProfPicker
                                control={control}
                                name="prof_level"
                                required={false}/>
                        </FormControl>

                    </FC>

                    {/*/!* ConsultationConfigPicker *!/*/}
                    {/*<FC>*/}
                    {/*    <Typography variant="h6">Консультация</Typography>*/}
                    {/*    <ConsultationConfigPicker*/}
                    {/*        cls={''}*/}
                    {/*        defaultConsultationConfigId={*/}
                    {/*            savedData.consultation_config ? savedData.consultation_config.id : 2*/}
                    {/*        }*/}
                    {/*        control={control}*/}
                    {/*        setValue={setValue}*/}
                    {/*        watch={watch}*/}
                    {/*        defaultSupervision={false}*/}
                    {/*        defaultIsOffline={false}*/}
                    {/*        hideSupervision={true}*/}
                    {/*        hideOffline={true}*/}
                    {/*    />*/}
                    {/*</FC>*/}

                    {/* Comfort Preference */}
                    <FormControl component="fieldset" className={'fc gap-1'}>
                        <Typography variant="h6">С кем вам комфортнее работать?</Typography>
                        <Controller
                            name="comfort_preference"
                            control={control}
                            render={({field}) => (
                                <RadioLine
                                    itemClass={'text-nowrap'}
                                    options={[
                                        {value: 'any', label: 'Все равно'},
                                        {value: 'specific', label: 'Есть пожелания'},
                                    ]}
                                    selectedValue={field.value ?? null}
                                    onChange={(value) => field.onChange(value)}
                                />
                            )}
                        />
                    </FormControl>

                    {comfort_preference === 'specific' && (
                        <Box className={'fr flex-wrap gap-3'}>
                            {/* Gender Preference */}
                            <FormControl component="fieldset" className={'fc gap-1'}>
                                <span>Пол психолога</span>
                                <Controller
                                    name="gender_preference"
                                    control={control}
                                    render={({field}) => (
                                        <RadioLine
                                            itemClass={'text-nowrap'}
                                            options={[
                                                {value: '', label: 'Не важно'},
                                                {value: 'male', label: 'Мужчина'},
                                                {value: 'female', label: 'Женщина'},
                                            ]}
                                            selectedValue={field.value ?? null}
                                            onChange={(value) => field.onChange(value)}
                                        />
                                    )}
                                />
                            </FormControl>

                            {/* Age Preference */}
                            <FormControl component="fieldset" className={'fc gap-1'}>
                                <span>Возраст психолога</span>
                                <Controller
                                    name="age_preference"
                                    control={control}
                                    render={({field}) => (
                                        <RadioLine
                                            maxWidth={100}
                                            className="w-100 fr gap-2 flex-wrap"
                                            itemClass={'flex-grow-1 rounded-3 text-nowrap'}
                                            options={[
                                                {value: '', label: 'Не важно'},
                                                {value: 'under_25', label: 'До 25 лет'},
                                                {value: '25_35', label: '25-35 лет'},
                                                {value: '35_45', label: '35-45 лет'},
                                                {value: '45_55', label: '45-55 лет'},
                                                {value: 'over_55', label: 'От 55 лет'},
                                            ]}
                                            selectedValue={field.value ?? null}
                                            onChange={(value) => field.onChange(value)}
                                        />
                                    )}
                                />
                            </FormControl>

                            {/* Specializations */}
                            <FC>
                                <Typography variant="h6">Специализации психолога</Typography>
                                <SpecializationsPicker control={control}/>
                            </FC>
                        </Box>
                    )}

                    {/*/!* Session Time Preference *!/*/}
                    {/*<FormControl component="fieldset" className={'fc gap-1'}>*/}
                    {/*    <Typography variant="h6">Предпочтение по времени консультации</Typography>*/}
                    {/*    <Controller*/}
                    {/*        name="session_time"*/}
                    {/*        control={control}*/}
                    {/*        render={({field}) => (*/}
                    {/*            <RadioLine*/}
                    {/*                options={[*/}
                    {/*                    {value: 'any', label: 'Любое'},*/}
                    {/*                    {value: 'nearest', label: 'Ближайшее'},*/}
                    {/*                    {value: 'specific', label: 'Приблизительное'},*/}
                    {/*                ]}*/}
                    {/*                selectedValue={field.value ?? null}*/}
                    {/*                onChange={(value) => field.onChange(value)}*/}
                    {/*            />*/}
                    {/*        )}*/}
                    {/*    />*/}
                    {/*</FormControl>*/}

                    {session_time === 'specific' && (
                        <FormControl component="fieldset" className={'fc gap-1'}>
                            <CustomDateTimePicker
                                label="Дата и время"
                                value={specificDateTime}
                                onChange={(newValue) => {
                                    setSpecificDateTime(newValue);
                                    setValue('specific_datetime', newValue?.toISOString() || '');
                                }}
                            />
                        </FormControl>
                    )}

                    {/* Хочу, чтобы у психолога были дети */}
                    <FormControl component="fieldset" className={'fc gap-1'}>
                        <Typography variant="h6">Хочу, чтобы у психолога были дети</Typography>
                        <Controller
                            name="is_psychologist_has_child"
                            control={control}
                            render={({field}) => (
                                <RadioLine
                                    itemClass={'text-nowrap'}
                                    options={[
                                        {value: 'not_important', label: 'Не важно'},
                                        {value: 'yes', label: 'Да'},
                                        {value: 'no', label: 'Нет'},
                                    ]}
                                    selectedValue={field.value ?? 'not_important'}
                                    onChange={(value) => field.onChange(value)}
                                />
                            )}
                        />
                    </FormControl>

                    {/* Skills Picker */}
                    <FC g={1}>
                        <Typography variant="h6">Выберите темы для обсуждения</Typography>
                        <SkillsPicker control={control}/>

                        <Button size={'small'} className={'opacity-50 w-min text-nowrap'} style={{marginBottom: '3rem'}}
                                onClick={() => {
                                    delFilterFromClientRequest();
                                    window.location.reload();
                                }}>Очистка фильтра
                        </Button>
                    </FC>
                </FC>
            </FC>

            {/* Интеграция компонента BottomButton */}
            <BottomButton
                cls={"maxw-340px w-90 mx-auto"}
                onClick={handleSubmit(onSubmit)}
                disabled={
                    !isValid ||
                    selectedSkills.length === 0 ||
                    (session_time === "specific" && (!specificDateTime))

                }
                containerRef={containerRef}
            >
                Далее
            </BottomButton>
        </FC>
    );
};

export default PersonalInfo;
